import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Users, Star, SlidersHorizontal, X } from "lucide-react";
import { api, getMe } from "../lib/api";
import { cachedBusinessGet, readCachedBusinesses, applyClientBusinessFilters } from "../lib/businessCache";
import { CATEGORY_CHIPS, haversineKm, fmtDistanceWalk } from "../lib/geo";
import { useGps } from "../lib/useGps";
import { TonightCard, PlaceRow } from "../components/PlaceCard";
import { BottomNav } from "../components/BottomNav";
import LegalFooter from "../components/LegalFooter";
import { LogoMark } from "../components/Logo";
import HeaderActions from "../components/HeaderActions";
import BannerCarousel from "../components/BannerCarousel";
import ExchangeRateStrip from "../components/ExchangeRateStrip";
import WeatherWidget from "../components/WeatherWidget";
import GuestUrgentBanner from "../components/GuestUrgentBanner";
import LanguagePrompt from "../components/LanguagePrompt";
import FilterPlacesSheet, { DEFAULT_FILTERS, isDefaultFilters, applyFilters } from "../components/FilterPlacesSheet";
import { useI18n } from "../lib/i18n";
import { useMode } from "../lib/appMode";
import RideMode from "./RideMode";
/** Editorial section header — matches mockup:
 *    <serif-italic gold small>Editorial</serif-italic-gold-small>
 *    <serif big white>Tonight in <i gold>Havana</i></serif>            See all →
 */
function SectionHeader({ eyebrow, title, accent, to, seeAllText }) {
  return (
    <div className="flex items-end justify-between gap-4 px-6">
      <div>
        <div className="font-display-italic text-gold text-lg leading-none mb-2.5">
          {eyebrow}
        </div>
        <h2 className="font-display text-[2.25rem] sm:text-[2.5rem] leading-[0.95] text-foreground tracking-tight">
          {accent ? (
            <>
              {title} <span className="font-display-italic text-gold">{accent}</span>
            </>
          ) : (
            title
          )}
        </h2>
      </div>
      {to && (
        <Link
          to={to}
          className="text-[13px] font-display-italic text-gold whitespace-nowrap hover:opacity-80 transition-opacity shrink-0 pb-2"
        >
          {seeAllText || "See all →"}
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  const [mode] = useMode();
  const [user, setUser] = useState(null);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [tonight, setTonight] = useState([]);
  // iter121 — Seed places from the cache on mount so offline users
  // never see an empty grid even before the failed network round-trip
  // resolves. The effect below will still overwrite this once the API
  // (or cache fallback) returns.
  const [places, setPlaces] = useState(() => readCachedBusinesses() || []);
  const [headerBanner, setHeaderBanner] = useState(null);
  const [footerBanner, setFooterBanner] = useState(null);
  const [recent, setRecent] = useState([]); // recent check-ins for logged-in users
  const [tonightRec, setTonightRec] = useState(null);
  const { coords } = useGps();
  const [unread, setUnread] = useState(0);
  const [editorialMsg, setEditorialMsg] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filtering, setFiltering] = useState(false); // 150ms skeleton state

  useEffect(() => {
    // iter198 — Single-round-trip cold-load via /api/bootstrap
    // (replaces 5 parallel fan-out calls: /auth/me, /public-settings,
    // /banners/active x2, /notifications). Non-critical calls still fan
    // out because they hit /businesses cache + /tonight-promotion which
    // aren't part of the bootstrap payload.
    api.get("/bootstrap").then(({ data }) => {
      const me = data?.me || null;
      setUser(me);
      const bnr = data?.banners || {};
      setHeaderBanner(bnr.header || null);
      setFooterBanner(bnr.footer || null);
      const settings = data?.settings || {};
      const msg = lang === "es"
        ? (settings.editorial_message_es || settings.editorial_message || settings.home_editorial_message_es || settings.home_editorial_message || "")
        : (settings.editorial_message || settings.home_editorial_message || "");
      setEditorialMsg((msg || "").trim());
      setUnread(data?.unread_notifications || 0);
      if (me) {
        api.get("/journey/visits?limit=8")
          .then(({ data }) => {
            const seen = new Set();
            const uniq = [];
            for (const v of data || []) {
              if (!v.business || seen.has(v.business_id)) continue;
              seen.add(v.business_id);
              uniq.push(v);
              if (uniq.length >= 5) break;
            }
            setRecent(uniq);
          })
          .catch(() => {});
      }
    }).catch(() => {
      // Bootstrap failed — fall back to individual calls so degraded
      // networks (offline SW) still render.
      getMe().then((me) => setUser(me)).catch(() => {});
      api.get("/banners/active?placement=header").then(({ data }) => setHeaderBanner(data)).catch(() => {});
      api.get("/banners/active?placement=footer").then(({ data }) => setFooterBanner(data)).catch(() => {});
    });

    // iter118 — Pre-warm the offline cache with the full businesses list.
    cachedBusinessGet("/businesses?tonight=true", "tonight")
      .then(({ data }) => setTonight(data));
    cachedBusinessGet("/businesses", "businesses").catch(() => {});

    api.get("/tonight-promotion").then(({ data }) => setTonightRec(data || null)).catch(() => {});
    // Poll unread every 30s (initial value from bootstrap above).
    const fetchUnread = () => api.get("/notifications").then(({ data }) => setUnread(data?.unread || 0)).catch(() => {});
    const i = setInterval(fetchUnread, 30000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (search) params.set("q", search);
    // iter118 — When the filtered URL fails offline, fall back to the
    // full cached business list filtered client-side.
    cachedBusinessGet(`/businesses?${params.toString()}`, "businesses").then(({ data, fromCache }) => {
      // Server-side filter result OR cached fallback set.
      if (!fromCache) {
        setPlaces(Array.isArray(data) ? data : []);
        return;
      }
      // iter121 — When falling back to cache, apply the *same* filters
      // client-side. If a category filter eliminated everything from the
      // tiny cached sample, surface the full cached set instead of
      // an empty grid — the user's intent (browse offline) wins over
      // strict filter accuracy on stale data.
      const cachedAll = readCachedBusinesses() || [];
      const cf = applyClientBusinessFilters(cachedAll, {
        category: category === "all" ? null : category,
        search,
      });
      if (cf.length === 0 && cachedAll.length > 0 && category === "all" && !search) {
        setPlaces(cachedAll);
      } else {
        setPlaces(cf);
      }
    });
  }, [category, search]);

  // 150ms shimmer when filters change to prevent list jump/flash
  useEffect(() => {
    setFiltering(true);
    const t = setTimeout(() => setFiltering(false), 150);
    return () => clearTimeout(t);
  }, [filters]);

  // Apply client-side filters + sort via the shared helper.
  const filtered = useMemo(() => applyFilters(places, filters, { coords }), [places, filters, coords]);

  // Active filter pill list
  const activePills = useMemo(() => {
    const out = [];
    if (filters.openNow) out.push({ key: "openNow", label: "Open Now", clear: () => setFilters((f) => ({ ...f, openNow: false })) });
    if (filters.vipOnly) out.push({ key: "vipOnly", label: "VIP", clear: () => setFilters((f) => ({ ...f, vipOnly: false })) });
    if (filters.cigarFriendly) out.push({ key: "cigar", label: "Cigar Friendly", clear: () => setFilters((f) => ({ ...f, cigarFriendly: false })) });
    if (filters.distance !== "any") {
      const lbl = filters.distance === "near" ? "Nearby" : "Walking";
      out.push({ key: "distance", label: lbl, clear: () => setFilters((f) => ({ ...f, distance: "any" })) });
    }
    if (filters.minRating !== "any") {
      const lbl = filters.minRating === "4plus" ? "4+ Stars" : "3+ Stars";
      out.push({ key: "minRating", label: lbl, clear: () => setFilters((f) => ({ ...f, minRating: "any" })) });
    }
    if (filters.sortBy !== "nearest") {
      const lbl = { rating: "Highest Rated", popular: "Most Popular", recent: "Recently Added" }[filters.sortBy];
      if (lbl) out.push({ key: "sort", label: `Sort: ${lbl}`, clear: () => setFilters((f) => ({ ...f, sortBy: "nearest" })) });
    }
    return out;
  }, [filters]);

  // Tapping the search bar resets chips + filter pills back to defaults.
  const onSearchFocus = () => {
    setCategory("all");
    setFilters(DEFAULT_FILTERS);
  };

  // Results count line copy
  const categoryLabelFor = (c) => CATEGORY_CHIPS.find((x) => x.key === c)?.label || c;
  const resultsLabel = useMemo(() => {
    const n = filtered.length;
    if (n === 0) return "No results — try removing a filter";
    if (search) return `${n} ${n === 1 ? "place" : "places"} matching ${JSON.stringify(search).slice(1, -1)}`;
    const parts = [];
    if (filters.openNow) parts.push("open now");
    if (filters.vipOnly) parts.push("VIP");
    if (filters.minRating === "4plus") parts.push("4+ Stars");
    if (filters.minRating === "3plus") parts.push("3+ Stars");
    const cat = category !== "all" ? categoryLabelFor(category) : "places";
    if (parts.length === 0) return `${n} ${cat} nearby`;
    return `${n} ${parts.join(" ")} ${cat.toLowerCase()} nearby`.replace("places", "places");
  }, [filtered.length, filters, category, search]);

  const recentlyVisitedHidden = category !== "all" || !!search;

  const distanceFor = (b) => {
    if (!coords || b.lat == null || b.lng == null) return null;
    return fmtDistanceWalk(haversineKm(coords, { lat: b.lat, lng: b.lng }));
  };

  return (
    <>
    {mode === "ride" && <RideMode />}
    {mode === "discover" && (
    <main data-testid="home-page" className="min-h-screen pb-32 bg-background">
      {/* 1 — HEADER — boxed H + HABANO wordmark + icons + avatar */}
      <header className="px-6 pt-6 pb-3 flex items-center justify-between">
        <Link to="/" data-testid="home-logo" aria-label="Habano">
          <LogoMark />
        </Link>
        <div className="flex items-center gap-3">
          <HeaderActions user={user} testidPrefix="home" />
        </div>
      </header>

      {/* 2 — ACTIVE BANNER HEADER SLOT — only when an active banner exists */}
      {headerBanner && (
        <div className="mt-2" data-testid="home-banner-header">
          <BannerCarousel banner={headerBanner} placement="header" />
        </div>
      )}

      {/* 3 — CUBA EXCHANGE RATE + HAVANA WEATHER — slim pills, FORCED side-by-side */}
      <div
        className="flex flex-nowrap items-center justify-between"
        style={{ padding: "0 16px", marginTop: 12, gap: 8 }}
        data-testid="home-info-row"
      >
        <ExchangeRateStrip />
        <WeatherWidget />
      </div>

      {/* 3a — Browser-language pill: one-time nudge for Spanish-speaking travelers */}
      <LanguagePrompt />

      {/* 3b — GUEST URGENT BANNER — only shown to logged-out customers with active Urgent/Customs/Currency update */}
      {!user && <GuestUrgentBanner />}

      {/* 4 — TONIGHT IN HAVANA — featured carousel */}
      <section className="mt-6" data-testid="tonight-section">
        <SectionHeader eyebrow={t("home_editorial")} title={t("home_tonight_in")} accent={t("home_havana")} to="/tonight" seeAllText={t("home_see_all")} />
        {tonightRec && tonightRec.business_id ? (
          <Link
            to={`/places/${tonightRec.business_id}`}
            data-testid="tonight-recommendation"
            className="block px-6 mt-4"
          >
            <div className="flex items-start gap-2.5">
              <Star
                className="w-3.5 h-3.5 text-gold shrink-0 mt-1"
                strokeWidth={1.5}
                style={{ fill: "#E8B84B" }}
              />
              <span
                className="font-display italic text-gold text-[15px] sm:text-base leading-snug line-clamp-2"
                style={{ quotes: "none" }}
              >
                {tonightRec.text?.[lang] || tonightRec.text?.en}
              </span>
            </div>
          </Link>
        ) : null}
        {tonight.length === 0 ? (
          <div className="px-6 mt-6 text-base text-foreground/45" data-testid="tonight-empty">
            {t("home_no_picks")}
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto no-scrollbar px-6 snap-x snap-mandatory">
            <div className="flex gap-5 min-w-max pb-2">
              {tonight.map((b) => (
                <TonightCard key={b.id} b={b} distance={distanceFor(b)} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* 5 — EDITORIAL STRIP — admin-curated, only when set */}
      {editorialMsg ? (
        <button
          type="button"
          onClick={() => document.querySelector('[data-testid="tonight-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
          data-testid="editorial-strip"
          className="block w-full text-left px-6 mt-2"
          style={{ minHeight: 48 }}
        >
          <div
            className="flex items-start"
            style={{
              background: "#171717",
              borderLeft: "3px solid #E8B84B",
              borderTopRightRadius: 8,
              borderBottomRightRadius: 8,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              padding: "10px 14px 10px 16px",
            }}
          >
            <span
              className="font-display italic text-[15px] sm:text-base leading-snug text-foreground/85 line-clamp-2"
              style={{ quotes: "none" }}
            >
              {editorialMsg.replace(/^["'\u2018\u2019\u201C\u201D\[\]\u00AB\u00BB\s\u2014\-]+/, "")}
            </span>
          </div>
        </button>
      ) : null}

      {/* 6 — RECENTLY VISITED RAIL — only for logged-in users with prior check-ins; hidden when filtering */}
      {user && recent.length > 0 && !recentlyVisitedHidden && (
        <section className="mt-10" data-testid="recently-visited-section">
          <SectionHeader eyebrow={t("home_recently_visited_eyebrow")} title={t("home_recently_title")} accent={t("home_recently_accent")} to="/journey" seeAllText={t("home_see_all")} />
          <div className="mt-6 overflow-x-auto no-scrollbar px-6 snap-x snap-mandatory">
            <div className="flex gap-5 min-w-max pb-2">
              {recent.map((v) => (
                <TonightCard
                  key={v.id}
                  b={v.business}
                  distance={distanceFor(v.business)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7 — SEARCH CAPSULE — tapping resets chip to All and clears active filter pills */}
      <div className="px-6 mt-6" data-testid="search-row">
        <div className="search-capsule flex items-center px-5 h-14">
          <Search className="w-4 h-4 text-foreground/45 shrink-0" strokeWidth={1.5} />
          <input
            data-testid="home-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={onSearchFocus}
            placeholder={t("home_search_placeholder")}
            className="flex-1 ml-3 bg-transparent text-base text-foreground placeholder:text-foreground/40 outline-none tracking-wide"
            style={{ fontSize: 16 }}
          />
        </div>
      </div>

      {/* 8 — CATEGORY SEGMENTED PILLS — sticky below header on scroll; filter icon on right */}
      <div
        className="mt-3 sticky z-20"
        style={{ top: 0, background: "#0B0B0B", paddingTop: 8, paddingBottom: 8 }}
        data-testid="category-chips-sticky"
      >
        <div className="flex items-center gap-2 pl-6 pr-3">
          <div className="overflow-x-auto no-scrollbar flex-1" data-testid="category-chips">
            <div className="flex gap-3 min-w-max pr-2">
              {CATEGORY_CHIPS.map((c) => {
                const active = c.key === category;
                return (
                  <button
                    key={c.key}
                    data-testid={`chip-${c.key}`}
                    onClick={() => {
                      if (c.key === "all") {
                        setCategory("all");
                        setFilters(DEFAULT_FILTERS);
                        setSearch("");
                      } else {
                        setCategory(c.key);
                      }
                    }}
                    className={
                      active
                        ? "px-6 py-2.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-medium transition-all duration-300 seg-item-active"
                        : "px-6 py-2.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-medium transition-all duration-300 text-[#7E766D] hover:text-foreground border border-gold-soft hover:border-gold-strong"
                    }
                    style={{ minHeight: 48 }}
                  >
                    {t(c.tKey) || c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            data-testid="filter-open-btn"
            onClick={() => setFilterOpen(true)}
            aria-label="Filter places"
            className="relative w-12 h-12 grid place-items-center rounded-full border border-gold-soft hover:border-gold-strong transition-colors shrink-0"
            style={{ minWidth: 48, minHeight: 48 }}
          >
            <SlidersHorizontal className="w-[18px] h-[18px] text-gold" strokeWidth={1.5} />
            {!isDefaultFilters(filters) && (
              <span
                aria-hidden="true"
                data-testid="filter-active-dot"
                style={{ position: "absolute", top: 8, right: 8, width: 8, height: 8, borderRadius: 9999, background: "#E8B84B" }}
              />
            )}
          </button>
        </div>
      </div>

      {/* 9 — RESULTS COUNT LINE */}
      <div
        className="px-6 mt-3"
        data-testid="results-count"
        style={{ color: "#B0AAA3", fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}
      >
        {resultsLabel}
      </div>

      {/* 10 — ACTIVE FILTER PILLS — only when non-default filters applied */}
      {activePills.length > 0 && (
        <div
          className="px-6 mt-2 flex flex-wrap gap-2"
          data-testid="active-filter-pills"
        >
          {activePills.map((p) => (
            <button
              type="button"
              key={p.key}
              data-testid={`filter-pill-active-${p.key}`}
              onClick={p.clear}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 transition-opacity hover:opacity-80"
              style={{
                background: "rgba(232,184,75,0.12)",
                border: "1px solid rgba(232,184,75,0.4)",
                color: "#E8B84B",
                fontSize: 11,
                fontFamily: "'DM Sans', sans-serif",
                minHeight: 32,
              }}
            >
              <span>{p.label}</span>
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          ))}
        </div>
      )}

      {/* 11 — NEARBY / DISCOVER */}
      <section className="mt-3" data-testid="nearby-section">
        {filtering ? (
          <div className="mt-3 px-6 space-y-0">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-5 py-5 -mx-4 px-4" data-testid="skeleton-row">
                <div className="w-[88px] h-[88px] rounded-2xl shimmer" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-2/3 rounded shimmer" />
                  <div className="h-3 w-1/2 rounded shimmer" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-12 text-center" data-testid="nearby-empty">
            <h3 className="font-display text-2xl text-foreground">
              {category !== "all"
                ? `No ${categoryLabelFor(category)} places found`
                : "No places match"}
            </h3>
            <p className="mt-2 text-foreground/55" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Try adjusting your filters or explore all places
            </p>
            <div className="mt-5 flex flex-col items-center gap-3">
              <button
                type="button"
                data-testid="empty-clear-filters"
                onClick={() => setFilters(DEFAULT_FILTERS)}
                className="rounded-full px-6"
                style={{ background: "#E8B84B", color: "#0B0B0B", minHeight: 48, fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}
              >
                Clear filters
              </button>
              <button
                type="button"
                data-testid="empty-explore-all"
                onClick={() => { setCategory("all"); setFilters(DEFAULT_FILTERS); setSearch(""); }}
                className="text-gold hover:opacity-80 transition-opacity"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, minHeight: 48 }}
              >
                Explore all places
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 px-6 space-y-0">
            {filtered.slice(0, 20).map((b) => (
              <PlaceRow key={b.id} b={b} distance={distanceFor(b)} />
            ))}
          </div>
        )}
      </section>

      {/* 9 — ACTIVE BANNER FOOTER SLOT — only when an active banner exists */}
      {footerBanner && (
        <div className="mt-16" data-testid="home-banner-footer">
          <BannerCarousel banner={footerBanner} placement="footer" />
        </div>
      )}

      <FilterPlacesSheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
        currentFilters={filters}
        businesses={places}
        resultCount={filtered.length}
      />

      <BottomNav />
      <LegalFooter force />
    </main>
    )}
    </>
  );
}
